import React from 'react';
import { useAuthenticate } from './useAuthenticate';
import { locationsDB, remoteDB } from '../integrations/pouch/DBConn';
import { getSessionInfo } from '../utils/getSessionInfo';
import { handleCouchError } from '../utils/handleCouchError';
import { Tracking } from '../analytics/tracking';
import { EVENTS } from '../utils/events/index';
import { debounce } from 'lodash';
import { captureException } from '../integrations/sentry/capture';
import { locationsFilter, optsLocations } from '../integrations/pouch/opts';
import { EQuerykeys } from '../models';
import { invalidators } from '../integrations/react_query/invalidators';
import { useQueryClient } from 'react-query';

const DB_NAME = 'locations';

const getDebouncedTrackChange = () =>
	debounce((docsLength: number) => {
		Tracking.capture(EVENTS.REPLICATION.CHANGE(DB_NAME), {
			direction: 'pull',
			docCount: docsLength,
			timestamp: Date.now(),
		});
	}, 10000);

export const useReplicateLocations = () => {
	const [isReplicating, setIsReplicating] = React.useState(false);

	const replicateRef = React.useRef<PouchDB.Replication.Replication<{}> | null>(null);
	const liveReplicationRef = React.useRef<PouchDB.Replication.Replication<{}> | null>(null);
	const isAuthenticated = useAuthenticate();
	const { userInfo, locationId } = getSessionInfo();
	const queryClient = useQueryClient();

	const debouncedTrackChange = getDebouncedTrackChange();
	const updateOnChange = invalidators(queryClient)[EQuerykeys.locations];

	const filter = locationsFilter();

	React.useEffect(() => {
		if (!isAuthenticated || !userInfo || !locationId) return;

		setIsReplicating(true);

		Tracking.capture(EVENTS.REPLICATION.STARTED(DB_NAME), {
			userId: userInfo?._id,
			dbName: DB_NAME,
			direction: 'from',
			startedAt: new Date().toISOString(),
		});

		const localDB = locationsDB();
		const remote = remoteDB(userInfo.merchantName);

		replicateRef.current = localDB.replicate
			.from(remote, {
				...optsLocations({ ...filter, live: undefined, retry: true }),
				timeout: 3000,
			})
			.on('complete', (info) => {
				if (info.status === 'complete') {
					Tracking.capture(EVENTS.REPLICATION.completed(DB_NAME), {
						userId: userInfo?._id,
						dbName: DB_NAME,
						direction: 'from',
						status: 'complete',
						replicatedAt: new Date().toISOString(),
						type: 'one-time',
					});

					setIsReplicating(false);

					liveReplicationRef.current = localDB.replicate
						.from(remote, optsLocations({ ...filter, live: true, retry: true }))
						.on('error', (err: {}) => {
							Tracking.capture(EVENTS.REPLICATION.FAILED(DB_NAME), {
								userId: userInfo?._id,
								dbName: DB_NAME,
								direction: 'from',
								status: 'error',
								error: err,
								type: 'live',
							});
							handleCouchError(err);
							captureException(err, { fn: 'useReplicateLocations.liveReplication.error' });
						})
						.on('change', async (info: PouchDB.Replication.ReplicationResult<{}>) => {
							debouncedTrackChange(info.docs?.length ?? 0);
							await updateOnChange();
						});
				}
			})
			.on('error', (error) => {
				Tracking.capture(EVENTS.REPLICATION.FAILED(DB_NAME), {
					userId: userInfo?._id,
					dbName: DB_NAME,
					direction: 'from',
					status: 'error',
					error: error,
					type: 'one-time',
				});
				handleCouchError(error);
				captureException(error, { fn: 'useReplicateLocations.oneTimeReplication.error' });
			})
			.on('change', async (info: PouchDB.Replication.ReplicationResult<{}>) => {
				debouncedTrackChange(info.docs?.length ?? 0);
				await updateOnChange();
			})
			.on('denied', (info: Record<string, unknown>) => {
				captureException(new Error('replication_denied'), {
					fn: 'useReplicateLocations.replication.denied',
					args: { info },
				});
			});

		return () => {
			replicateRef.current?.cancel();
			liveReplicationRef.current?.cancel();
		};
	}, []);

	return { isReplicating };
};
